// Portfolio Administration v1.0 PR 6 — cross-portfolio Loan Tape monthly
// close page. Replaces the PR 0 ComingSoonPage at
// /portfolio-admin/loan-tape. Behind PortfolioAdminV1.
//
// WS-4 legacy-tape retirement: the tape is read-only historical data + PSA
// export. All write affordances (bulk lock, per-row entry actions) are gone.

import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Info } from "lucide-react";
import { Button, Card } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlag } from "@/hooks/portfolioAdmin/useFeatureFlag";
import { useLoanTapePeriodView } from "@/hooks/portfolioAdmin/usePr6";
import { ComingSoonPage } from "@/components/ComingSoonPage";
import { LoanTapeFilters } from "@/components/portfolioAdmin/loanTape/LoanTapeFilters";
import {
  LOAN_TAPE_STATUS_OPTIONS,
  LoanTapeStatusSummary,
} from "@/components/portfolioAdmin/loanTape/LoanTapeStatusSummary";
import { LoanTapeTable } from "@/components/portfolioAdmin/loanTape/LoanTapeTable";
import { PsaLoanTapeExportModal } from "@/components/portfolioAdmin/loanTape/PsaLoanTapeExportModal";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

const PAGE_SIZE = 50;

function thisMonth(tz: string): string {
  return isoDateInTz(new Date(), tz).slice(0, 7);
}

function recentPeriods(months = 12): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = 0; i < months; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    out.push(d.toISOString().slice(0, 7));
  }
  return out;
}

function arrayParam(params: URLSearchParams, key: string): string[] {
  const raw = params.getAll(key);
  if (raw.length === 0) return [];
  return raw.flatMap((v) => v.split(",")).filter(Boolean);
}

export default function LoanTapePage() {
  const flagOn = useFeatureFlag("PortfolioAdminV1");
  if (!flagOn) {
    return (
      <ComingSoonPage
        feature="Loan Tape"
        pr="PR 6"
        description="Monthly payment-entry surface and the canonical handoff to investor reporting and accounting."
        backTo="/portfolio-admin/loans"
        backLabel="Back to Loans"
      />
    );
  }
  return <LoanTapePageInner />;
}

function LoanTapePageInner() {
  const auth = useAuth();
  const tz = useUserTimeZone();
  const canExport =
    auth.hasPermission("LoanTape.Export") || auth.hasRoleCode("ADMIN");

  const [params, setParams] = useSearchParams();
  const periods = useMemo(() => recentPeriods(12), []);
  const period = params.get("period") || thisMonth(tz);
  const statuses = arrayParam(params, "status");
  const variance =
    params.get("variance") === "yes"
      ? true
      : params.get("variance") === "no"
        ? false
        : undefined;
  const minDaysLateRaw = params.get("daysLate");
  const minDaysLate = minDaysLateRaw ? Number(minDaysLateRaw) : undefined;
  const search = params.get("search") ?? "";

  const writeParams = (mutate: (p: URLSearchParams) => void) => {
    const next = new URLSearchParams(params);
    mutate(next);
    setParams(next, { replace: true });
  };

  const setPeriod = (p: string) =>
    writeParams((q) => {
      q.set("period", p);
    });

  const setStatuses = (next: string[]) =>
    writeParams((q) => {
      q.delete("status");
      next.forEach((s) => q.append("status", s));
    });

  const setVariance = (v: boolean | undefined) =>
    writeParams((q) => {
      if (v === undefined) q.delete("variance");
      else q.set("variance", v ? "yes" : "no");
    });

  const setMinDaysLate = (n: number | undefined) =>
    writeParams((q) => {
      if (n === undefined) q.delete("daysLate");
      else q.set("daysLate", String(n));
    });

  const setSearch = (s: string) =>
    writeParams((q) => {
      if (s) q.set("search", s);
      else q.delete("search");
    });

  const query = useLoanTapePeriodView({
    period,
    statuses: statuses.length ? statuses : undefined,
    variance,
    minDaysLate,
    search: search || undefined,
  });
  const entries = useMemo(() => query.data ?? [], [query.data]);

  const [page, setPage] = useState(0);
  const [exportOpen, setExportOpen] = useState(false);

  const toggleStatus = (status: string) => {
    const set = new Set(statuses);
    if (set.has(status)) set.delete(status);
    else set.add(status);
    setStatuses(Array.from(set));
    setPage(0);
  };

  return (
    <div
      className="flex flex-col gap-space-3"
      data-testid="loan-tape-page"
    >
      <div className="flex items-center justify-between gap-space-2 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-h1 m-0">Loan Tape (Monthly Close)</h1>
          <p className="text-small text-gray-mid m-0">
            Cross-portfolio monthly close. Click a row to drill into the
            loan's Payment Tracking tab. For the incoming-payment ledger, see
            Finance → Loan Payments.
          </p>
        </div>
        <div className="flex items-center gap-space-2">
          {canExport && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setExportOpen(true)}
              data-testid="psa-export-open"
            >
              Export PSA Loan Tape CSV
            </Button>
          )}
        </div>
      </div>

      <div
        className="flex gap-space-2 items-start bg-status-info/10 border border-status-info/30 rounded-md px-space-3 py-space-2"
        role="note"
        data-testid="loan-tape-readonly-banner"
      >
        <Info size={14} className="text-status-info mt-[2px] shrink-0" aria-hidden />
        <p className="text-small m-0">
          The legacy loan tape is read-only historical data; payments are now
          recorded in the payment ledger. PSA export remains available.
        </p>
      </div>

      <Card className="flex flex-col gap-space-3">
        <LoanTapeFilters
          period={period}
          recentPeriods={periods}
          variance={variance}
          minDaysLate={minDaysLate}
          search={search}
          onPeriodChange={(p) => {
            setPeriod(p);
            setPage(0);
          }}
          onVarianceChange={(v) => {
            setVariance(v);
            setPage(0);
          }}
          onMinDaysLateChange={(n) => {
            setMinDaysLate(n);
            setPage(0);
          }}
          onSearchChange={(s) => {
            setSearch(s);
            setPage(0);
          }}
        />
        <LoanTapeStatusSummary
          entries={entries}
          selectedStatuses={statuses}
          onToggleStatus={toggleStatus}
        />
      </Card>

      {statuses.length > 0 && (
        <div
          className="flex items-center gap-space-1 text-caption text-gray-mid"
          data-testid="loan-tape-active-filters"
        >
          Status filter:{" "}
          {statuses.map((s) => {
            const opt = LOAN_TAPE_STATUS_OPTIONS.find((o) => o.value === s);
            return (
              <span
                key={s}
                className="px-space-2 py-[2px] border border-border-light rounded-sm bg-white-pure"
              >
                {opt?.label ?? s}
              </span>
            );
          })}
          <button
            type="button"
            onClick={() => {
              setStatuses([]);
              setPage(0);
            }}
            className="text-gold-shadow hover:underline"
          >
            Clear
          </button>
        </div>
      )}

      {query.isLoading ? (
        <p className="text-small text-gray-mid">Loading Loan Tape…</p>
      ) : query.isError ? (
        <p
          className="text-small text-status-error"
          role="alert"
          data-testid="loan-tape-error"
        >
          Failed to load Loan Tape entries.
        </p>
      ) : (
        <LoanTapeTable
          entries={entries}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      )}

      <PsaLoanTapeExportModal
        open={exportOpen}
        period={period}
        entries={entries}
        onClose={() => setExportOpen(false)}
      />
    </div>
  );
}
